import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto!")
		.max(50, "Muito longo!")
		.required("Digite um nome!"),
});

const FlowBuilderModal = ({ open, onClose, integrationId, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const [contact, setContact] = useState({
		name: "",
		type: "flowbuilder",
	});

	// Canal do fluxo: decide quais blocos a barra lateral do construtor
	// oferece. Vale so na criacao; fluxo que ja existe nao muda.
	const [canal, setCanal] = useState("whatsapp");

	useEffect(() => {
		(async () => {
			if (!integrationId) return;
			try {
				const { data } = await api.get(`/flowbuilder/${integrationId}`);
				setContact({
					name: data.name || "",
					type: "flowbuilder",
				});
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			isMounted.current = false;
			setContact({
				name: "",
				type: "flowbuilder",
			});
		};
	}, [integrationId, open]);

	const handleClose = () => {
		onClose();
		setContact({
			name: "",
			type: "flowbuilder",
		});
	};

	const handleSaveContact = async values => {
		try {
			const payload = {
				name: values.name,
				canal
			};

			if (integrationId) {
				await api.put(`/flowbuilder`, { flowId: integrationId, name: values.name });
				toast.success("Fluxo atualizado com sucesso!");
			} else {
				await api.post("/flowbuilder", payload);
				toast.success("Fluxo criado com sucesso!");
			}
			
			if (onSave) onSave();
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};
	
	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} fullWidth="md" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{integrationId
						? `Editar Fluxo`
						: `Adicionar Fluxo`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									defaultValue={contact.name}
									error={Boolean(errors.name)}
									helperText={errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
									style={{width: '95%'}}
								/>

								{!integrationId && (
									<div style={{ width: "95%", marginTop: 14 }}>
										<div
											style={{
												fontSize: 13,
												fontWeight: 600,
												marginBottom: 8,
												opacity: 0.85
											}}
										>
											Onde este fluxo vai rodar?
										</div>
										<div style={{ display: "flex", gap: 10 }}>
											{[
												{
													v: "whatsapp",
													t: "WhatsApp",
													d: "Todos os blocos do WhatsApp"
												},
												{
													v: "meta",
													t: "Instagram e Facebook",
													d: "So o que essas redes aceitam"
												}
											].map(op => (
												<div
													key={op.v}
													onClick={() => setCanal(op.v)}
													style={{
														flex: 1,
														cursor: "pointer",
														padding: "10px 12px",
														borderRadius: 8,
														border:
															canal === op.v
																? "2px solid #1976d2"
																: "1px solid rgba(128,128,128,0.4)",
														background:
															canal === op.v
																? "rgba(25,118,210,0.08)"
																: "transparent"
													}}
												>
													<div style={{ fontSize: 13.5, fontWeight: 600 }}>
														{op.t}
													</div>
													<div style={{ fontSize: 11.5, opacity: 0.7 }}>
														{op.d}
													</div>
												</div>
											))}
										</div>
										<div
											style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}
										>
											Isso muda apenas quais blocos aparecem enquanto voce
											monta. Nao impede de usar o fluxo em outra conexao.
										</div>
									</div>
								)}

							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{integrationId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default FlowBuilderModal;
